import React, { useRef } from 'react';
import { motion, useMotionValue, useTransform, useAnimation, PanInfo } from 'framer-motion';
import { Pencil, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';

interface SwipeAction {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  className?: string;
  destructive?: boolean;
}

interface SwipeableRowProps {
  children: React.ReactNode;
  leftActions?: SwipeAction[];
  rightActions?: SwipeAction[];
  className?: string;
  actionWidth?: number;
  threshold?: number;
}

export function SwipeableRow({
  children,
  leftActions = [],
  rightActions = [],
  className,
  actionWidth = 80,
  threshold = 0.3,
}: SwipeableRowProps) {
  const x = useMotionValue(0);
  const controls = useAnimation();
  const containerRef = useRef<HTMLDivElement>(null);

  const maxLeftSwipe = leftActions.length * actionWidth;
  const maxRightSwipe = rightActions.length * actionWidth;

  // Transform for revealing actions
  const leftActionsX = useTransform(x, [0, maxLeftSwipe], [-maxLeftSwipe, 0]);
  const rightActionsX = useTransform(x, [-maxRightSwipe, 0], [0, maxRightSwipe]);

  const handleDragEnd = async (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const swipeDistance = info.offset.x;
    const velocity = info.velocity.x;

    // Determine if we should snap open or closed
    if (swipeDistance > maxLeftSwipe * threshold || velocity > 500) {
      // Snap open left actions
      await controls.start({ x: maxLeftSwipe });
    } else if (swipeDistance < -maxRightSwipe * threshold || velocity < -500) {
      // Snap open right actions
      await controls.start({ x: -maxRightSwipe });
    } else {
      // Snap closed
      await controls.start({ x: 0 });
    }
  };

  const closeActions = () => {
    controls.start({ x: 0 });
  };

  return (
    <div ref={containerRef} className={cn('relative overflow-hidden', className)}>
      {/* Left Actions (revealed when swiping right) */}
      {leftActions.length > 0 && (
        <motion.div
          className="absolute inset-y-0 left-0 flex"
          style={{ x: leftActionsX }}
        >
          {leftActions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                action.onClick();
                closeActions();
              }}
              className={cn(
                'flex flex-col items-center justify-center text-white text-xs font-medium',
                action.destructive ? 'bg-red-500' : 'bg-blue-500',
                action.className
              )}
              style={{ width: actionWidth }}
            >
              {action.icon}
              <span className="mt-1">{action.label}</span>
            </button>
          ))}
        </motion.div>
      )}

      {/* Right Actions (revealed when swiping left) */}
      {rightActions.length > 0 && (
        <motion.div
          className="absolute inset-y-0 right-0 flex"
          style={{ x: rightActionsX }}
        >
          {rightActions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                action.onClick();
                closeActions();
              }}
              className={cn(
                'flex flex-col items-center justify-center text-white text-xs font-medium',
                action.destructive ? 'bg-red-500' : 'bg-blue-500',
                action.className
              )}
              style={{ width: actionWidth }}
            >
              {action.icon}
              <span className="mt-1">{action.label}</span>
            </button>
          ))}
        </motion.div>
      )}

      {/* Main Content */}
      <motion.div
        className="relative bg-background"
        drag="x"
        dragConstraints={{
          left: -maxRightSwipe,
          right: maxLeftSwipe,
        }}
        dragElastic={0.1}
        onDragEnd={handleDragEnd}
        animate={controls}
        style={{ x }}
      >
        {children}
      </motion.div>
    </div>
  );
}

// Preset action creators for common use cases
export const createEditAction = (onClick: () => void): SwipeAction => ({
  icon: <Pencil className="h-5 w-5" />,
  label: 'Edit',
  onClick,
  className: 'bg-blue-500',
});

export const createDeleteAction = (onClick: () => void): SwipeAction => ({
  icon: <Trash2 className="h-5 w-5" />,
  label: 'Delete',
  onClick,
  destructive: true,
});

export default SwipeableRow;
